import { FC } from "react";

import { Box, Badge, BoxProps } from "@hightouch/htui";
import { Link } from "src/router";

import { IntegrationIcon } from "src/components/integrations/integration-icon";
import placeholderSource from "src/components/permission/source.svg";
import {
  SourceDefinitionFragment,
  SourceObjectDefinitionFragment,
  SourceObjectDefinitionsQueryVariables,
  SourceQuery,
  useSourceObjectDefinitionsQuery,
} from "src/graphql";
import { useSchemaDiscovery } from "./use-schema-discovery";
import { HTImage } from "src/components/image";

export type SourceConfig = Record<string, unknown>;

export type TableKeySource = {
  definition: Pick<
    SourceDefinitionFragment,
    "type" | "supportsCrossDatabaseDiscovery"
  >;
  config: SourceConfig;
};

// Warehouses treat database names as case-insensitive.
export const getObjectDefinitionKey = (
  objectDefinition: Pick<
    SourceObjectDefinitionFragment,
    "name" | "object_definition_group"
  >,
  source?: TableKeySource,
): string => {
  const schemaGroup = objectDefinition.object_definition_group;
  if (!schemaGroup) {
    return objectDefinition.name;
  }
  const database = schemaGroup.database;
  const databaseConfigKey =
    source?.definition.type === "databricks" ? "catalog" : "database";
  const configuredDatabase = source?.config[databaseConfigKey];
  if (
    source?.definition.supportsCrossDatabaseDiscovery &&
    database &&
    typeof configuredDatabase === "string" &&
    database.toLowerCase() !== configuredDatabase.toLowerCase()
  ) {
    return `${database}.${schemaGroup.name}.${objectDefinition.name}`;
  }
  return `${schemaGroup.name}.${objectDefinition.name}`;
};

export type SourceTunnel = NonNullable<
  NonNullable<SourceQuery["connections_by_pk"]>["tunnel"]
>;

// Each dot-separated piece must match the database, schema, or table name.
export const parseSearch = (
  search?: string,
  options?: { matchDatabase?: boolean },
): Omit<SourceObjectDefinitionsQueryVariables, "sourceId"> => {
  const [first, second, ...rest] = search?.split(".") ?? [];
  const parts = [first, second, rest.join(".")].filter(Boolean);
  const terms = parts.map((part) => `%${part}%`);
  // '' matches no database; null would make the branch match-all.
  const databaseTerms = terms.map((term) =>
    options?.matchDatabase ? term : "",
  );
  return {
    term1: terms[0],
    term2: terms[1],
    term3: terms[2],
    termDatabase1: databaseTerms[0],
    termDatabase2: databaseTerms[1],
    termDatabase3: databaseTerms[2],
  };
};

export const useObjectDefinitions = (
  sourceId: string | undefined,
  enable = true,
  search?: string,
  searchOptions?: { matchDatabase?: boolean },
) => {
  const {
    data: objectDefinitionsData,
    isFetching: objectDefinitionsLoading,
    error: objectDefinitionsError,
    refetch: refetchObjectDefinitions,
  } = useSourceObjectDefinitionsQuery(
    { sourceId: sourceId ?? "", ...parseSearch(search, searchOptions) },
    { enabled: Boolean(sourceId) && enable },
  );

  const { discovering, runSourceDiscovery, discoverError } = useSchemaDiscovery(
    {
      connectionId: sourceId ?? "",
    },
  );

  const objectDefinitions = objectDefinitionsData?.object_definitions;

  const objectDefinitionOptions =
    objectDefinitions?.map(({ name, object_definition_group }) => ({
      label: `${object_definition_group?.name}.${name}`,
      value: `${object_definition_group?.name}.${name}`,
    })) || [];

  const refetch = async () => {
    try {
      if (sourceId) {
        await runSourceDiscovery();
      }
      refetchObjectDefinitions();
    } catch (error) {
      console.log(error);
    }
  };

  return {
    refetch,
    objectDefinitions,
    objectDefinitionOptions,
    loading: discovering || objectDefinitionsLoading,
    error: (discoverError || objectDefinitionsError)?.message,
  };
};

type SourceTileProps = {
  source?: {
    name: string;
    id: string;
    definition: {
      name: string;
      icon: string;
      isSampleDataSource: boolean;
    };
  };
  placeholder?: boolean;
};

export const SourceTile: FC<Readonly<SourceTileProps>> = ({
  source,
  placeholder,
}) => {
  const name = !source && placeholder ? "Private source" : source?.name;

  return (
    <>
      <IntegrationIcon
        name={source?.definition?.name}
        src={source?.definition?.icon}
      />
      <Box
        sx={{
          fontWeight: "semi",
          fontSize: "lg",
          ml: 2,
          a: { color: "inherit", fontSize: "inherit", fontWeight: "inherit" },
        }}
      >
        {source?.id ? (
          <Link href={`/sources/${source?.id}`}>{name}</Link>
        ) : (
          name
        )}
      </Box>

      <SourceBadges
        isSampleDataSource={source?.definition.isSampleDataSource}
      />
    </>
  );
};

export function SourceIcon({
  placeholder,
  source,
  sx = {},
}: Readonly<{
  placeholder?: boolean;
  source:
    | {
        definition: {
          name: string;
          icon: string;
        };
      }
    | undefined
    | null;
  sx: BoxProps["sx"];
}>): JSX.Element | null {
  const alt =
    placeholder && !source ? "Private source" : source?.definition?.name;
  const src =
    placeholder && !source ? placeholderSource : source?.definition?.icon;
  return (
    <HTImage
      alt={alt || ""}
      src={src || ""}
      sx={{ ...sx, objectFit: "contain" }}
    />
  );
}

export function SourceBadges({
  isSampleDataSource,
}: Readonly<{
  isSampleDataSource: boolean | undefined;
  sx?: BoxProps["sx"];
}>): JSX.Element | null {
  return isSampleDataSource ? (
    <Badge ml={2} variant="warning">
      Sample
    </Badge>
  ) : null;
}
